/**
 * EditProfile — anchor port of devourplay-anchor/edit-profile-modal.html.
 *
 * Body section of the avatar+nickname modal. Renders the eyebrow + period
 * title header, the avatar strip with file-constraint caption, and the
 * nickname field with helper copy + character counter. The legacy
 * `EditProfileAvatarEdit` is kept so the drag/drop + remove-avatar logic
 * survives untouched; SCSS overrides reshape its chrome to anchor.
 */

import React from "react";
import { DragEvents } from "@/components/modals/editProfileModal/dragEvents";
import { IoClose } from "react-icons/io5";
import { EditProfileAvatarEdit } from "./EditProfileAvatarEdit";

interface Props {
    onClose: () => void;
    handleDragEvents: (event: DragEvents) => React.DragEventHandler<HTMLDivElement>;
    count: number;
    dragState: DragEvents;
    inputName: string;
    setInputName: (val: string) => void;
    removeAvatar: () => Promise<void>;
    setShowPfpOptions: (val: boolean) => void;
    nameError?: string | null;
}

const NICKNAME_MAX = 24;

export default function EditProfile(props: Props) {
    const value = props.inputName ?? "";
    return (
        <>
            <header className="edit-profile-modal_header">
                <div className="edit-profile-modal_header_body">
                    <span className="edit-profile-modal_header_eyebrow">Profile</span>
                    <h2 className="edit-profile-modal_header_title">Edit Profile.</h2>
                </div>
                <button
                    type="button"
                    className="edit-profile-modal_header_icon"
                    aria-label="Close"
                    onClick={props.onClose}
                >
                    <IoClose className="icon-close" />
                </button>
            </header>

            <div className="edit-profile-modal_body">
                <div className="edit-profile-modal_body_avatar-strip">
                    <EditProfileAvatarEdit
                        canRemove={false}
                        count={props.count}
                        dragState={props.dragState}
                        isOnEditMode={false}
                        handleDragEvents={props.handleDragEvents}
                        onEdit={() => props.setShowPfpOptions(true)}
                        removeAvatar={props.removeAvatar}
                    />
                    <p className="edit-profile-modal_body_avatar-note">
                        PNG or JPG · square works best · max 4 MB
                    </p>
                </div>

                <div className="edit-profile-modal_body_field">
                    <span className="edit-profile-modal_body_field-lbl">Nickname</span>
                    <input
                        className="edit-profile-modal_body_field-input"
                        type="text"
                        value={value}
                        maxLength={NICKNAME_MAX}
                        placeholder="Your nickname…"
                        onChange={(e) => props.setInputName(e.target.value)}
                    />
                    {props.nameError &&
                        <p className="edit-profile-modal_body_field-error">{props.nameError}</p>
                    }
                    <p className="edit-profile-modal_body_field-help">
                        Your display name across DevourPlay — chat, leaderboards, Drops. Letters, numbers, hyphens, underscores.
                    </p>
                    <span className="edit-profile-modal_body_field-counter">
                        {value.length} / {NICKNAME_MAX}
                    </span>
                </div>
            </div>
        </>
    );
}
